// The Inbox: its list, its rows and the Inbox canvas (POLISH PL-15, DESIGN-SPEC §6.6, §7).
//
// Namespace `inbox`; owner PL-15. The page title and sidebar label stay the shell's view
// label until the shell is localized. Placeholders are `{name}`, filled with `formatCopy`.
// See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  // The page
  needsSome: 'Decisions others ask you for come first.',
  needsNone: 'Nothing needs you right now.',
  loading: 'Loading your Inbox…',
  needsYou: 'Needs you',
  updates: 'Updates',
  waiting: 'Waiting on others',
  decisions: 'Recent decisions',
  decisionsFailed: 'Recent decisions could not be loaded.',
  markAllRead: 'Mark all read',
  emptyTitle: 'Nothing here yet',
  emptyBody: 'Reviews, approvals and news about your work arrive here.',
  unread: 'Unread',
  // A row that stands for several updates of the same kind, e.g. one agent's runs
  latestOf: 'Latest of {count}',
  // The sidebar entry
  badgeNeeds: 'Needs you: {count}',
  newUpdates: 'New updates',
  // Each item's kind
  kindSharingReview: 'Sharing review',
  kindSkillPublish: 'Publish a Skill',
  kindPage: 'Knowledge page to review',
  kindEditAccess: 'Edit access request',
  kindSharedWithYou: 'Shared with you',
  kindMadePrivate: 'Made private',
  kindAgentRun: 'Agent run',
  kindAgentVersion: 'Agent version',
  kindEditGranted: 'Edit access',
  kindAgentOwnership: 'Agent ownership',
  kindAgentSchedule: 'Agent schedule',
  kindYourRequest: 'Your request',
  kindFollowUp: 'Suggested follow-up',
  kindResultEdited: 'Result edited',
  kindMeetingReady: 'Meeting ready',
  kindOther: 'Inbox',
  // A request's state
  approved: 'Approved',
  declined: 'Declined',
  pending: 'Waiting for a decision',
  untitled: 'Proposed company change',
  // The canvas footer
  pendingFooter: 'Nothing changes until you approve.',
  waitingFooter: 'Waiting for a publisher to decide.',
  approveVersion: 'Approve this version',
  decline: 'Decline',
  approvedToast: 'Approved.',
  declinedToast: 'Declined.',
  published: 'Approved version published.',
  rejected: 'Request declined. The item stays unchanged.',
  // The canvas body
  submitted: 'The version submitted for review. Later changes need a new request.',
  submittedDecided: 'The version submitted with this request.',
  technicalDetails: 'Technical details',
  readFull: 'Read the full text',
  previousPage: 'Previous page',
  nextPage: 'Next page',
  pageNote: 'A page of the submitted text.',
  contentStart: 'The beginning of the submitted text.',
  tooLong: 'This is too long to show here in full. Open it to read all of it and decide there.',
  shortened: 'This is the start. Open it to read the rest.',
  earlier: 'Earlier',
  others: 'Others like this',
  open: 'Open',
  openRun: 'Open the run',
  openMeeting: 'Open the meeting',
  gone: 'This is no longer in your Inbox',
  goneBody: 'It was decided, withdrawn or removed elsewhere.',
  opening: 'Opening…',
  actionDone: 'Done.',
  actionFailed: 'That did not work. Try again.',
  readFailed: 'We could not mark that as read.',
  openFailed: 'We could not open this request.',
};

export type InboxCopyKey = keyof typeof en;
export type InboxCopy = Readonly<Record<InboxCopyKey, string>>;

const inboxCopy: NamespaceCatalog<InboxCopyKey> = {
  bg: {
    needsSome: 'Първо са решенията, които други чакат от вас.',
    needsNone: 'В момента нищо не ви чака.',
    loading: 'Входящите се зареждат…',
    needsYou: 'Чака вас',
    updates: 'Новини',
    waiting: 'Чака други',
    decisions: 'Последни решения',
    decisionsFailed: 'Последните решения не можаха да се заредят.',
    markAllRead: 'Маркиране на всички като прочетени',
    emptyTitle: 'Все още няма нищо',
    emptyBody: 'Тук пристигат прегледи, одобрения и новини за работата ви.',
    unread: 'Непрочетено',
    latestOf: 'Последно от {count}',
    badgeNeeds: 'Чака вас: {count}',
    newUpdates: 'Нови новини',
    kindSharingReview: 'Преглед на споделяне',
    kindSkillPublish: 'Публикуване на Skill',
    kindPage: 'Страница със знания за преглед',
    kindEditAccess: 'Заявка за достъп за редактиране',
    kindSharedWithYou: 'Споделено с вас',
    kindMadePrivate: 'Направено лично',
    kindAgentRun: 'Изпълнение на агент',
    kindAgentVersion: 'Версия на агент',
    kindEditGranted: 'Достъп за редактиране',
    kindAgentOwnership: 'Собственост на агент',
    kindAgentSchedule: 'График на агент',
    kindYourRequest: 'Вашата заявка',
    kindFollowUp: 'Предложена следваща стъпка',
    kindResultEdited: 'Редактиран резултат',
    kindMeetingReady: 'Срещата е готова',
    kindOther: 'Входящи',
    approved: 'Одобрено',
    declined: 'Отказано',
    pending: 'Чака решение',
    untitled: 'Предложена промяна за компанията',
    pendingFooter: 'Нищо не се променя, докато не одобрите.',
    waitingFooter: 'Чака решение от публикуващ.',
    approveVersion: 'Одобряване на тази версия',
    decline: 'Отказ',
    approvedToast: 'Одобрено.',
    declinedToast: 'Отказано.',
    published: 'Одобрената версия е публикувана.',
    rejected: 'Заявката е отказана. Елементът остава непроменен.',
    submitted: 'Версията, изпратена за преглед. По-късните промени изискват нова заявка.',
    submittedDecided: 'Версията, изпратена с тази заявка.',
    technicalDetails: 'Технически подробности',
    readFull: 'Прочитане на целия текст',
    previousPage: 'Предишна страница',
    nextPage: 'Следваща страница',
    pageNote: 'Страница от изпратения текст.',
    contentStart: 'Началото на изпратения текст.',
    tooLong:
      'Това е твърде дълго, за да се покаже изцяло тук. Отворете го, за да го прочетете и решите там.',
    shortened: 'Това е началото. Отворете, за да прочетете останалото.',
    earlier: 'По-рано',
    others: 'Други подобни',
    open: 'Отваряне',
    openRun: 'Отваряне на изпълнението',
    openMeeting: 'Отваряне на срещата',
    gone: 'Това вече не е във входящите ви',
    goneBody: 'Решено, оттеглено или премахнато е другаде.',
    opening: 'Отваряне…',
    actionDone: 'Готово.',
    actionFailed: 'Не се получи. Опитайте отново.',
    readFailed: 'Не успяхме да маркираме това като прочетено.',
    openFailed: 'Не успяхме да отворим тази заявка.',
  },
  hr: {
    needsSome: 'Najprije odluke koje drugi traže od vas.',
    needsNone: 'Trenutačno ništa ne čeka vas.',
    loading: 'Učitavanje pristigle pošte…',
    needsYou: 'Čeka vas',
    updates: 'Novosti',
    waiting: 'Čeka druge',
    decisions: 'Nedavne odluke',
    decisionsFailed: 'Nedavne odluke nije bilo moguće učitati.',
    markAllRead: 'Označi sve kao pročitano',
    emptyTitle: 'Još ništa',
    emptyBody: 'Ovdje stižu pregledi, odobrenja i novosti o vašem radu.',
    unread: 'Nepročitano',
    latestOf: 'Najnovije od {count}',
    badgeNeeds: 'Čeka vas: {count}',
    newUpdates: 'Nove novosti',
    kindSharingReview: 'Pregled dijeljenja',
    kindSkillPublish: 'Objava Skilla',
    kindPage: 'Stranica znanja za pregled',
    kindEditAccess: 'Zahtjev za pristup uređivanju',
    kindSharedWithYou: 'Podijeljeno s vama',
    kindMadePrivate: 'Postavljeno privatnim',
    kindAgentRun: 'Pokretanje agenta',
    kindAgentVersion: 'Verzija agenta',
    kindEditGranted: 'Pristup uređivanju',
    kindAgentOwnership: 'Vlasništvo agenta',
    kindAgentSchedule: 'Raspored agenta',
    kindYourRequest: 'Vaš zahtjev',
    kindFollowUp: 'Predloženi sljedeći korak',
    kindResultEdited: 'Rezultat uređen',
    kindMeetingReady: 'Sastanak je spreman',
    kindOther: 'Pristigla pošta',
    approved: 'Odobreno',
    declined: 'Odbijeno',
    pending: 'Čeka odluku',
    untitled: 'Predložena promjena za tvrtku',
    pendingFooter: 'Ništa se ne mijenja dok ne odobrite.',
    waitingFooter: 'Čeka odluku objavljivača.',
    approveVersion: 'Odobri ovu verziju',
    decline: 'Odbij',
    approvedToast: 'Odobreno.',
    declinedToast: 'Odbijeno.',
    published: 'Odobrena verzija je objavljena.',
    rejected: 'Zahtjev je odbijen. Stavka ostaje nepromijenjena.',
    submitted: 'Verzija poslana na pregled. Kasnije promjene trebaju novi zahtjev.',
    submittedDecided: 'Verzija poslana s ovim zahtjevom.',
    technicalDetails: 'Tehnički detalji',
    readFull: 'Pročitaj cijeli tekst',
    previousPage: 'Prethodna stranica',
    nextPage: 'Sljedeća stranica',
    pageNote: 'Stranica poslanog teksta.',
    contentStart: 'Početak poslanog teksta.',
    tooLong:
      'Ovo je predugo da bi se ovdje prikazalo u cijelosti. Otvorite ga da biste ga pročitali i ondje odlučili.',
    shortened: 'Ovo je početak. Otvorite da biste pročitali ostatak.',
    earlier: 'Ranije',
    others: 'Ostalo slično',
    open: 'Otvori',
    openRun: 'Otvori pokretanje',
    openMeeting: 'Otvori sastanak',
    gone: 'Ovo više nije u vašoj pristigloj pošti',
    goneBody: 'Odlučeno je, povučeno ili uklonjeno na drugom mjestu.',
    opening: 'Otvaranje…',
    actionDone: 'Gotovo.',
    actionFailed: 'Nije uspjelo. Pokušajte ponovno.',
    readFailed: 'Nismo mogli označiti ovo kao pročitano.',
    openFailed: 'Nismo mogli otvoriti ovaj zahtjev.',
  },
  cs: {
    needsSome: 'Nejdřív rozhodnutí, o která vás žádají ostatní.',
    needsNone: 'Teď na vás nic nečeká.',
    loading: 'Načítání doručené pošty…',
    needsYou: 'Čeká na vás',
    updates: 'Novinky',
    waiting: 'Čeká na ostatní',
    decisions: 'Nedávná rozhodnutí',
    decisionsFailed: 'Nedávná rozhodnutí se nepodařilo načíst.',
    markAllRead: 'Označit vše jako přečtené',
    emptyTitle: 'Zatím tu nic není',
    emptyBody: 'Sem přicházejí kontroly, schválení a novinky o vaší práci.',
    unread: 'Nepřečteno',
    latestOf: 'Nejnovější ze {count}',
    badgeNeeds: 'Čeká na vás: {count}',
    newUpdates: 'Nové novinky',
    kindSharingReview: 'Kontrola sdílení',
    kindSkillPublish: 'Zveřejnění Skillu',
    kindPage: 'Stránka znalostí ke kontrole',
    kindEditAccess: 'Žádost o přístup k úpravám',
    kindSharedWithYou: 'Sdíleno s vámi',
    kindMadePrivate: 'Nastaveno jako soukromé',
    kindAgentRun: 'Běh agenta',
    kindAgentVersion: 'Verze agenta',
    kindEditGranted: 'Přístup k úpravám',
    kindAgentOwnership: 'Vlastnictví agenta',
    kindAgentSchedule: 'Plán agenta',
    kindYourRequest: 'Vaše žádost',
    kindFollowUp: 'Navržený další krok',
    kindResultEdited: 'Výsledek upraven',
    kindMeetingReady: 'Schůzka je připravena',
    kindOther: 'Doručená pošta',
    approved: 'Schváleno',
    declined: 'Zamítnuto',
    pending: 'Čeká na rozhodnutí',
    untitled: 'Navrhovaná změna pro firmu',
    pendingFooter: 'Nic se nezmění, dokud neschválíte.',
    waitingFooter: 'Čeká na rozhodnutí publikujícího.',
    approveVersion: 'Schválit tuto verzi',
    decline: 'Zamítnout',
    approvedToast: 'Schváleno.',
    declinedToast: 'Zamítnuto.',
    published: 'Schválená verze je zveřejněna.',
    rejected: 'Žádost zamítnuta. Položka zůstává beze změny.',
    submitted: 'Verze odeslaná ke kontrole. Pozdější změny vyžadují novou žádost.',
    submittedDecided: 'Verze odeslaná s touto žádostí.',
    technicalDetails: 'Technické podrobnosti',
    readFull: 'Přečíst celý text',
    previousPage: 'Předchozí stránka',
    nextPage: 'Další stránka',
    pageNote: 'Stránka odeslaného textu.',
    contentStart: 'Začátek odeslaného textu.',
    tooLong:
      'Tohle je příliš dlouhé, aby se to tu zobrazilo celé. Otevřete to, přečtěte si to a rozhodněte tam.',
    shortened: 'Tohle je začátek. Otevřete to a přečtěte si zbytek.',
    earlier: 'Dříve',
    others: 'Další podobné',
    open: 'Otevřít',
    openRun: 'Otevřít běh',
    openMeeting: 'Otevřít schůzku',
    gone: 'Tohle už ve vaší doručené poště není',
    goneBody: 'Bylo to rozhodnuto, staženo nebo odstraněno jinde.',
    opening: 'Otevírání…',
    actionDone: 'Hotovo.',
    actionFailed: 'Nepodařilo se to. Zkuste to znovu.',
    readFailed: 'Nepodařilo se to označit jako přečtené.',
    openFailed: 'Tuto žádost se nepodařilo otevřít.',
  },
  da: {
    needsSome: 'Beslutninger, andre beder dig om, kommer først.',
    needsNone: 'Intet venter på dig lige nu.',
    loading: 'Indlæser din indbakke…',
    needsYou: 'Venter på dig',
    updates: 'Nyheder',
    waiting: 'Venter på andre',
    decisions: 'Seneste beslutninger',
    decisionsFailed: 'De seneste beslutninger kunne ikke indlæses.',
    markAllRead: 'Markér alle som læst',
    emptyTitle: 'Intet her endnu',
    emptyBody: 'Gennemgange, godkendelser og nyt om dit arbejde lander her.',
    unread: 'Ulæst',
    latestOf: 'Seneste af {count}',
    badgeNeeds: 'Venter på dig: {count}',
    newUpdates: 'Nye nyheder',
    kindSharingReview: 'Gennemgang af deling',
    kindSkillPublish: 'Udgiv en Skill',
    kindPage: 'Vidensside til gennemgang',
    kindEditAccess: 'Anmodning om redigeringsadgang',
    kindSharedWithYou: 'Delt med dig',
    kindMadePrivate: 'Gjort privat',
    kindAgentRun: 'Agentkørsel',
    kindAgentVersion: 'Agentversion',
    kindEditGranted: 'Redigeringsadgang',
    kindAgentOwnership: 'Ejerskab af agent',
    kindAgentSchedule: 'Agentens tidsplan',
    kindYourRequest: 'Din anmodning',
    kindFollowUp: 'Foreslået næste skridt',
    kindResultEdited: 'Resultat redigeret',
    kindMeetingReady: 'Mødet er klar',
    kindOther: 'Indbakke',
    approved: 'Godkendt',
    declined: 'Afvist',
    pending: 'Venter på en beslutning',
    untitled: 'Foreslået ændring for virksomheden',
    pendingFooter: 'Intet ændres, før du godkender.',
    waitingFooter: 'Venter på, at en udgiver beslutter.',
    approveVersion: 'Godkend denne version',
    decline: 'Afvis',
    approvedToast: 'Godkendt.',
    declinedToast: 'Afvist.',
    published: 'Den godkendte version er udgivet.',
    rejected: 'Anmodningen er afvist. Elementet forbliver uændret.',
    submitted: 'Versionen sendt til gennemgang. Senere ændringer kræver en ny anmodning.',
    submittedDecided: 'Versionen sendt med denne anmodning.',
    technicalDetails: 'Tekniske detaljer',
    readFull: 'Læs hele teksten',
    previousPage: 'Forrige side',
    nextPage: 'Næste side',
    pageNote: 'En side af den indsendte tekst.',
    contentStart: 'Begyndelsen af den indsendte tekst.',
    tooLong:
      'Det er for langt til at blive vist helt her. Åbn det for at læse det hele og beslutte der.',
    shortened: 'Dette er begyndelsen. Åbn det for at læse resten.',
    earlier: 'Tidligere',
    others: 'Andre som denne',
    open: 'Åbn',
    openRun: 'Åbn kørslen',
    openMeeting: 'Åbn mødet',
    gone: 'Det er ikke længere i din indbakke',
    goneBody: 'Det er besluttet, trukket tilbage eller fjernet et andet sted.',
    opening: 'Åbner…',
    actionDone: 'Udført.',
    actionFailed: 'Det lykkedes ikke. Prøv igen.',
    readFailed: 'Vi kunne ikke markere det som læst.',
    openFailed: 'Vi kunne ikke åbne denne anmodning.',
  },
  nl: {
    needsSome: 'Beslissingen die anderen van je vragen staan bovenaan.',
    needsNone: 'Er wacht nu niets op jou.',
    loading: 'Je inbox wordt geladen…',
    needsYou: 'Wacht op jou',
    updates: 'Updates',
    waiting: 'Wacht op anderen',
    decisions: 'Recente beslissingen',
    decisionsFailed: 'Recente beslissingen konden niet worden geladen.',
    markAllRead: 'Alles als gelezen markeren',
    emptyTitle: 'Nog niets hier',
    emptyBody: 'Beoordelingen, goedkeuringen en nieuws over je werk komen hier binnen.',
    unread: 'Ongelezen',
    latestOf: 'Nieuwste van {count}',
    badgeNeeds: 'Wacht op jou: {count}',
    newUpdates: 'Nieuwe updates',
    kindSharingReview: 'Beoordeling van delen',
    kindSkillPublish: 'Een Skill publiceren',
    kindPage: 'Kennispagina om te beoordelen',
    kindEditAccess: 'Verzoek om bewerktoegang',
    kindSharedWithYou: 'Met jou gedeeld',
    kindMadePrivate: 'Privé gemaakt',
    kindAgentRun: 'Agentrun',
    kindAgentVersion: 'Agentversie',
    kindEditGranted: 'Bewerktoegang',
    kindAgentOwnership: 'Eigendom van agent',
    kindAgentSchedule: 'Planning van agent',
    kindYourRequest: 'Je verzoek',
    kindFollowUp: 'Voorgestelde vervolgstap',
    kindResultEdited: 'Resultaat bewerkt',
    kindMeetingReady: 'Vergadering klaar',
    kindOther: 'Inbox',
    approved: 'Goedgekeurd',
    declined: 'Afgewezen',
    pending: 'Wacht op een beslissing',
    untitled: 'Voorgestelde wijziging voor het bedrijf',
    pendingFooter: 'Er verandert niets tot je goedkeurt.',
    waitingFooter: 'Wacht op een beslissing van een publiceerder.',
    approveVersion: 'Deze versie goedkeuren',
    decline: 'Afwijzen',
    approvedToast: 'Goedgekeurd.',
    declinedToast: 'Afgewezen.',
    published: 'Goedgekeurde versie gepubliceerd.',
    rejected: 'Verzoek afgewezen. Het item blijft ongewijzigd.',
    submitted:
      'De versie die ter beoordeling is ingediend. Latere wijzigingen vragen een nieuw verzoek.',
    submittedDecided: 'De versie die met dit verzoek is ingediend.',
    technicalDetails: 'Technische details',
    readFull: 'Volledige tekst lezen',
    previousPage: 'Vorige pagina',
    nextPage: 'Volgende pagina',
    pageNote: 'Een pagina van de ingediende tekst.',
    contentStart: 'Het begin van de ingediende tekst.',
    tooLong:
      'Dit is te lang om hier volledig te tonen. Open het om alles te lezen en daar te beslissen.',
    shortened: 'Dit is het begin. Open het om de rest te lezen.',
    earlier: 'Eerder',
    others: 'Andere zoals deze',
    open: 'Openen',
    openRun: 'De run openen',
    openMeeting: 'De vergadering openen',
    gone: 'Dit staat niet meer in je inbox',
    goneBody: 'Het is elders beslist, ingetrokken of verwijderd.',
    opening: 'Openen…',
    actionDone: 'Klaar.',
    actionFailed: 'Dat is niet gelukt. Probeer het opnieuw.',
    readFailed: 'We konden dit niet als gelezen markeren.',
    openFailed: 'We konden dit verzoek niet openen.',
  },
  en,
  et: {
    needsSome: 'Esmalt otsused, mida teised sinult ootavad.',
    needsNone: 'Praegu ei oota sind midagi.',
    loading: 'Postkasti laadimine…',
    needsYou: 'Ootab sind',
    updates: 'Uudised',
    waiting: 'Ootab teisi',
    decisions: 'Hiljutised otsused',
    decisionsFailed: 'Hiljutisi otsuseid ei õnnestunud laadida.',
    markAllRead: 'Märgi kõik loetuks',
    emptyTitle: 'Siin pole veel midagi',
    emptyBody: 'Siia jõuavad ülevaatused, kinnitused ja uudised sinu töö kohta.',
    unread: 'Lugemata',
    latestOf: 'Uusim {count}-st',
    badgeNeeds: 'Ootab sind: {count}',
    newUpdates: 'Uued uudised',
    kindSharingReview: 'Jagamise ülevaatus',
    kindSkillPublish: 'Skilli avaldamine',
    kindPage: 'Ülevaatamist ootav teadmiste leht',
    kindEditAccess: 'Muutmisõiguse taotlus',
    kindSharedWithYou: 'Sinuga jagatud',
    kindMadePrivate: 'Muudetud privaatseks',
    kindAgentRun: 'Agendi käivitus',
    kindAgentVersion: 'Agendi versioon',
    kindEditGranted: 'Muutmisõigus',
    kindAgentOwnership: 'Agendi omandiõigus',
    kindAgentSchedule: 'Agendi ajakava',
    kindYourRequest: 'Sinu taotlus',
    kindFollowUp: 'Soovitatud järgmine samm',
    kindResultEdited: 'Tulemust muudeti',
    kindMeetingReady: 'Koosolek on valmis',
    kindOther: 'Postkast',
    approved: 'Kinnitatud',
    declined: 'Tagasi lükatud',
    pending: 'Ootab otsust',
    untitled: 'Ettevõttele pakutud muudatus',
    pendingFooter: 'Midagi ei muutu enne, kui kinnitad.',
    waitingFooter: 'Ootab avaldaja otsust.',
    approveVersion: 'Kinnita see versioon',
    decline: 'Lükka tagasi',
    approvedToast: 'Kinnitatud.',
    declinedToast: 'Tagasi lükatud.',
    published: 'Kinnitatud versioon avaldati.',
    rejected: 'Taotlus lükati tagasi. Üksus jääb muutmata.',
    submitted: 'Ülevaatuseks esitatud versioon. Hilisemad muudatused vajavad uut taotlust.',
    submittedDecided: 'Selle taotlusega esitatud versioon.',
    technicalDetails: 'Tehnilised üksikasjad',
    readFull: 'Loe kogu teksti',
    previousPage: 'Eelmine leht',
    nextPage: 'Järgmine leht',
    pageNote: 'Leht esitatud tekstist.',
    contentStart: 'Esitatud teksti algus.',
    tooLong:
      'See on liiga pikk, et siin täielikult näidata. Ava see, et kõike lugeda ja seal otsustada.',
    shortened: 'See on algus. Ava see, et ülejäänut lugeda.',
    earlier: 'Varem',
    others: 'Teised sarnased',
    open: 'Ava',
    openRun: 'Ava käivitus',
    openMeeting: 'Ava koosolek',
    gone: 'Seda pole enam sinu postkastis',
    goneBody: 'See otsustati, võeti tagasi või eemaldati mujal.',
    opening: 'Avamine…',
    actionDone: 'Valmis.',
    actionFailed: 'See ei õnnestunud. Proovi uuesti.',
    readFailed: 'Me ei saanud seda loetuks märkida.',
    openFailed: 'Me ei saanud seda taotlust avada.',
  },
  fi: {
    needsSome: 'Päätökset, joita muut pyytävät sinulta, ovat ensin.',
    needsNone: 'Mikään ei odota sinua juuri nyt.',
    loading: 'Ladataan saapuneita…',
    needsYou: 'Odottaa sinua',
    updates: 'Uutiset',
    waiting: 'Odottaa muita',
    decisions: 'Viimeaikaiset päätökset',
    decisionsFailed: 'Viimeaikaisia päätöksiä ei voitu ladata.',
    markAllRead: 'Merkitse kaikki luetuiksi',
    emptyTitle: 'Täällä ei ole vielä mitään',
    emptyBody: 'Tänne saapuvat tarkistukset, hyväksynnät ja uutiset työstäsi.',
    unread: 'Lukematon',
    latestOf: 'Uusin {count}:stä',
    badgeNeeds: 'Odottaa sinua: {count}',
    newUpdates: 'Uusia uutisia',
    kindSharingReview: 'Jakamisen tarkistus',
    kindSkillPublish: 'Skillin julkaisu',
    kindPage: 'Tarkistettava tietosivu',
    kindEditAccess: 'Muokkausoikeuspyyntö',
    kindSharedWithYou: 'Jaettu kanssasi',
    kindMadePrivate: 'Muutettu yksityiseksi',
    kindAgentRun: 'Agentin ajo',
    kindAgentVersion: 'Agentin versio',
    kindEditGranted: 'Muokkausoikeus',
    kindAgentOwnership: 'Agentin omistajuus',
    kindAgentSchedule: 'Agentin aikataulu',
    kindYourRequest: 'Pyyntösi',
    kindFollowUp: 'Ehdotettu seuraava vaihe',
    kindResultEdited: 'Tulosta muokattu',
    kindMeetingReady: 'Kokous on valmis',
    kindOther: 'Saapuneet',
    approved: 'Hyväksytty',
    declined: 'Hylätty',
    pending: 'Odottaa päätöstä',
    untitled: 'Ehdotettu muutos yritykselle',
    pendingFooter: 'Mikään ei muutu ennen kuin hyväksyt.',
    waitingFooter: 'Odottaa julkaisijan päätöstä.',
    approveVersion: 'Hyväksy tämä versio',
    decline: 'Hylkää',
    approvedToast: 'Hyväksytty.',
    declinedToast: 'Hylätty.',
    published: 'Hyväksytty versio julkaistiin.',
    rejected: 'Pyyntö hylättiin. Kohde pysyy ennallaan.',
    submitted: 'Tarkistettavaksi lähetetty versio. Myöhemmät muutokset vaativat uuden pyynnön.',
    submittedDecided: 'Tämän pyynnön mukana lähetetty versio.',
    technicalDetails: 'Tekniset tiedot',
    readFull: 'Lue koko teksti',
    previousPage: 'Edellinen sivu',
    nextPage: 'Seuraava sivu',
    pageNote: 'Sivu lähetetystä tekstistä.',
    contentStart: 'Lähetetyn tekstin alku.',
    tooLong:
      'Tämä on liian pitkä näytettäväksi tässä kokonaan. Avaa se lukeaksesi kaiken ja päätä siellä.',
    shortened: 'Tämä on alku. Avaa se lukeaksesi loput.',
    earlier: 'Aiemmin',
    others: 'Muut samanlaiset',
    open: 'Avaa',
    openRun: 'Avaa ajo',
    openMeeting: 'Avaa kokous',
    gone: 'Tämä ei ole enää saapuneissasi',
    goneBody: 'Se päätettiin, peruttiin tai poistettiin muualla.',
    opening: 'Avataan…',
    actionDone: 'Valmis.',
    actionFailed: 'Se ei onnistunut. Yritä uudelleen.',
    readFailed: 'Emme voineet merkitä sitä luetuksi.',
    openFailed: 'Emme voineet avata tätä pyyntöä.',
  },
  fr: {
    needsSome: 'Les décisions qu’on vous demande passent en premier.',
    needsNone: 'Rien ne vous attend pour le moment.',
    loading: 'Chargement de votre boîte de réception…',
    needsYou: 'Vous attend',
    updates: 'Nouvelles',
    waiting: 'En attente des autres',
    decisions: 'Décisions récentes',
    decisionsFailed: 'Impossible de charger les décisions récentes.',
    markAllRead: 'Tout marquer comme lu',
    emptyTitle: 'Rien pour l’instant',
    emptyBody: 'Les revues, les approbations et les nouvelles de votre travail arrivent ici.',
    unread: 'Non lu',
    latestOf: 'Le plus récent sur {count}',
    badgeNeeds: 'Vous attend : {count}',
    newUpdates: 'Nouvelles non lues',
    kindSharingReview: 'Revue de partage',
    kindSkillPublish: 'Publier un Skill',
    kindPage: 'Page de connaissances à revoir',
    kindEditAccess: 'Demande d’accès en modification',
    kindSharedWithYou: 'Partagé avec vous',
    kindMadePrivate: 'Rendu privé',
    kindAgentRun: 'Exécution d’agent',
    kindAgentVersion: 'Version d’agent',
    kindEditGranted: 'Accès en modification',
    kindAgentOwnership: 'Propriété de l’agent',
    kindAgentSchedule: 'Planification de l’agent',
    kindYourRequest: 'Votre demande',
    kindFollowUp: 'Suite suggérée',
    kindResultEdited: 'Résultat modifié',
    kindMeetingReady: 'Réunion prête',
    kindOther: 'Boîte de réception',
    approved: 'Approuvé',
    declined: 'Refusé',
    pending: 'En attente d’une décision',
    untitled: 'Modification proposée pour l’entreprise',
    pendingFooter: 'Rien ne change tant que vous n’approuvez pas.',
    waitingFooter: 'En attente de la décision d’un éditeur.',
    approveVersion: 'Approuver cette version',
    decline: 'Refuser',
    approvedToast: 'Approuvé.',
    declinedToast: 'Refusé.',
    published: 'Version approuvée publiée.',
    rejected: 'Demande refusée. L’élément reste inchangé.',
    submitted:
      'La version soumise pour revue. Les modifications ultérieures demandent une nouvelle demande.',
    submittedDecided: 'La version soumise avec cette demande.',
    technicalDetails: 'Détails techniques',
    readFull: 'Lire le texte complet',
    previousPage: 'Page précédente',
    nextPage: 'Page suivante',
    pageNote: 'Une page du texte soumis.',
    contentStart: 'Le début du texte soumis.',
    tooLong:
      'Ce contenu est trop long pour être affiché ici en entier. Ouvrez-le pour tout lire et décider là-bas.',
    shortened: 'Ceci est le début. Ouvrez-le pour lire la suite.',
    earlier: 'Plus tôt',
    others: 'Éléments similaires',
    open: 'Ouvrir',
    openRun: 'Ouvrir l’exécution',
    openMeeting: 'Ouvrir la réunion',
    gone: 'Cet élément n’est plus dans votre boîte de réception',
    goneBody: 'Il a été décidé, retiré ou supprimé ailleurs.',
    opening: 'Ouverture…',
    actionDone: 'Terminé.',
    actionFailed: 'Cela n’a pas fonctionné. Réessayez.',
    readFailed: 'Impossible de marquer cet élément comme lu.',
    openFailed: 'Impossible d’ouvrir cette demande.',
  },
  de: {
    needsSome: 'Entscheidungen, um die andere Sie bitten, stehen oben.',
    needsNone: 'Gerade wartet nichts auf Sie.',
    loading: 'Ihr Posteingang wird geladen…',
    needsYou: 'Wartet auf Sie',
    updates: 'Neuigkeiten',
    waiting: 'Wartet auf andere',
    decisions: 'Letzte Entscheidungen',
    decisionsFailed: 'Die letzten Entscheidungen konnten nicht geladen werden.',
    markAllRead: 'Alle als gelesen markieren',
    emptyTitle: 'Noch nichts hier',
    emptyBody: 'Prüfungen, Freigaben und Neuigkeiten zu Ihrer Arbeit kommen hier an.',
    unread: 'Ungelesen',
    latestOf: 'Neueste von {count}',
    badgeNeeds: 'Wartet auf Sie: {count}',
    newUpdates: 'Neue Neuigkeiten',
    kindSharingReview: 'Freigabeprüfung',
    kindSkillPublish: 'Skill veröffentlichen',
    kindPage: 'Wissensseite zur Prüfung',
    kindEditAccess: 'Anfrage auf Bearbeitungszugriff',
    kindSharedWithYou: 'Mit Ihnen geteilt',
    kindMadePrivate: 'Privat gemacht',
    kindAgentRun: 'Agentenlauf',
    kindAgentVersion: 'Agentenversion',
    kindEditGranted: 'Bearbeitungszugriff',
    kindAgentOwnership: 'Eigentum am Agenten',
    kindAgentSchedule: 'Zeitplan des Agenten',
    kindYourRequest: 'Ihre Anfrage',
    kindFollowUp: 'Vorgeschlagener nächster Schritt',
    kindResultEdited: 'Ergebnis bearbeitet',
    kindMeetingReady: 'Besprechung bereit',
    kindOther: 'Posteingang',
    approved: 'Freigegeben',
    declined: 'Abgelehnt',
    pending: 'Wartet auf eine Entscheidung',
    untitled: 'Vorgeschlagene Änderung für das Unternehmen',
    pendingFooter: 'Nichts ändert sich, bevor Sie freigeben.',
    waitingFooter: 'Wartet auf die Entscheidung einer veröffentlichenden Person.',
    approveVersion: 'Diese Version freigeben',
    decline: 'Ablehnen',
    approvedToast: 'Freigegeben.',
    declinedToast: 'Abgelehnt.',
    published: 'Freigegebene Version veröffentlicht.',
    rejected: 'Anfrage abgelehnt. Das Element bleibt unverändert.',
    submitted:
      'Die zur Prüfung eingereichte Version. Spätere Änderungen brauchen eine neue Anfrage.',
    submittedDecided: 'Die mit dieser Anfrage eingereichte Version.',
    technicalDetails: 'Technische Details',
    readFull: 'Ganzen Text lesen',
    previousPage: 'Vorherige Seite',
    nextPage: 'Nächste Seite',
    pageNote: 'Eine Seite des eingereichten Texts.',
    contentStart: 'Der Anfang des eingereichten Texts.',
    tooLong:
      'Das ist zu lang, um es hier vollständig zu zeigen. Öffnen Sie es, um alles zu lesen und dort zu entscheiden.',
    shortened: 'Das ist der Anfang. Öffnen Sie es, um den Rest zu lesen.',
    earlier: 'Früher',
    others: 'Weitere dieser Art',
    open: 'Öffnen',
    openRun: 'Lauf öffnen',
    openMeeting: 'Besprechung öffnen',
    gone: 'Das ist nicht mehr in Ihrem Posteingang',
    goneBody: 'Es wurde an anderer Stelle entschieden, zurückgezogen oder entfernt.',
    opening: 'Wird geöffnet…',
    actionDone: 'Erledigt.',
    actionFailed: 'Das hat nicht geklappt. Versuchen Sie es erneut.',
    readFailed: 'Wir konnten das nicht als gelesen markieren.',
    openFailed: 'Wir konnten diese Anfrage nicht öffnen.',
  },
  el: {
    needsSome: 'Πρώτα οι αποφάσεις που σας ζητούν άλλοι.',
    needsNone: 'Τίποτα δεν σας περιμένει αυτή τη στιγμή.',
    loading: 'Φόρτωση εισερχομένων…',
    needsYou: 'Σας περιμένει',
    updates: 'Νέα',
    waiting: 'Περιμένει άλλους',
    decisions: 'Πρόσφατες αποφάσεις',
    decisionsFailed: 'Δεν ήταν δυνατή η φόρτωση των πρόσφατων αποφάσεων.',
    markAllRead: 'Σήμανση όλων ως αναγνωσμένων',
    emptyTitle: 'Τίποτα ακόμη',
    emptyBody: 'Εδώ φτάνουν έλεγχοι, εγκρίσεις και νέα για τη δουλειά σας.',
    unread: 'Μη αναγνωσμένο',
    latestOf: 'Το πιο πρόσφατο από {count}',
    badgeNeeds: 'Σας περιμένει: {count}',
    newUpdates: 'Νέα ενημερώσεις',
    kindSharingReview: 'Έλεγχος κοινοποίησης',
    kindSkillPublish: 'Δημοσίευση Skill',
    kindPage: 'Σελίδα γνώσης προς έλεγχο',
    kindEditAccess: 'Αίτημα πρόσβασης επεξεργασίας',
    kindSharedWithYou: 'Κοινοποιήθηκε σε εσάς',
    kindMadePrivate: 'Έγινε ιδιωτικό',
    kindAgentRun: 'Εκτέλεση πράκτορα',
    kindAgentVersion: 'Έκδοση πράκτορα',
    kindEditGranted: 'Πρόσβαση επεξεργασίας',
    kindAgentOwnership: 'Κυριότητα πράκτορα',
    kindAgentSchedule: 'Πρόγραμμα πράκτορα',
    kindYourRequest: 'Το αίτημά σας',
    kindFollowUp: 'Προτεινόμενο επόμενο βήμα',
    kindResultEdited: 'Το αποτέλεσμα επεξεργάστηκε',
    kindMeetingReady: 'Η σύσκεψη είναι έτοιμη',
    kindOther: 'Εισερχόμενα',
    approved: 'Εγκρίθηκε',
    declined: 'Απορρίφθηκε',
    pending: 'Αναμένει απόφαση',
    untitled: 'Προτεινόμενη αλλαγή για την εταιρεία',
    pendingFooter: 'Τίποτα δεν αλλάζει μέχρι να εγκρίνετε.',
    waitingFooter: 'Αναμένει απόφαση από εκδότη.',
    approveVersion: 'Έγκριση αυτής της έκδοσης',
    decline: 'Απόρριψη',
    approvedToast: 'Εγκρίθηκε.',
    declinedToast: 'Απορρίφθηκε.',
    published: 'Η εγκεκριμένη έκδοση δημοσιεύτηκε.',
    rejected: 'Το αίτημα απορρίφθηκε. Το στοιχείο μένει αμετάβλητο.',
    submitted: 'Η έκδοση που υποβλήθηκε για έλεγχο. Μεταγενέστερες αλλαγές χρειάζονται νέο αίτημα.',
    submittedDecided: 'Η έκδοση που υποβλήθηκε με αυτό το αίτημα.',
    technicalDetails: 'Τεχνικές λεπτομέρειες',
    readFull: 'Ανάγνωση ολόκληρου του κειμένου',
    previousPage: 'Προηγούμενη σελίδα',
    nextPage: 'Επόμενη σελίδα',
    pageNote: 'Μια σελίδα του κειμένου που υποβλήθηκε.',
    contentStart: 'Η αρχή του κειμένου που υποβλήθηκε.',
    tooLong:
      'Είναι πολύ μεγάλο για να εμφανιστεί εδώ ολόκληρο. Ανοίξτε το για να το διαβάσετε όλο και να αποφασίσετε εκεί.',
    shortened: 'Αυτή είναι η αρχή. Ανοίξτε το για να διαβάσετε τα υπόλοιπα.',
    earlier: 'Νωρίτερα',
    others: 'Άλλα παρόμοια',
    open: 'Άνοιγμα',
    openRun: 'Άνοιγμα της εκτέλεσης',
    openMeeting: 'Άνοιγμα της σύσκεψης',
    gone: 'Αυτό δεν βρίσκεται πια στα εισερχόμενά σας',
    goneBody: 'Αποφασίστηκε, αποσύρθηκε ή αφαιρέθηκε αλλού.',
    opening: 'Άνοιγμα…',
    actionDone: 'Ολοκληρώθηκε.',
    actionFailed: 'Δεν λειτούργησε. Δοκιμάστε ξανά.',
    readFailed: 'Δεν μπορέσαμε να το σημειώσουμε ως αναγνωσμένο.',
    openFailed: 'Δεν μπορέσαμε να ανοίξουμε αυτό το αίτημα.',
  },
  hu: {
    needsSome: 'Elöl vannak a döntések, amelyeket mások kérnek öntől.',
    needsNone: 'Most semmi sem vár önre.',
    loading: 'A beérkezett elemek betöltése…',
    needsYou: 'Önre vár',
    updates: 'Hírek',
    waiting: 'Másokra vár',
    decisions: 'Legutóbbi döntések',
    decisionsFailed: 'A legutóbbi döntéseket nem sikerült betölteni.',
    markAllRead: 'Összes megjelölése olvasottként',
    emptyTitle: 'Még nincs itt semmi',
    emptyBody: 'Ide érkeznek az ellenőrzések, jóváhagyások és a munkájával kapcsolatos hírek.',
    unread: 'Olvasatlan',
    latestOf: 'A legújabb ennyiből: {count}',
    badgeNeeds: 'Önre vár: {count}',
    newUpdates: 'Új hírek',
    kindSharingReview: 'Megosztás ellenőrzése',
    kindSkillPublish: 'Skill közzététele',
    kindPage: 'Ellenőrizendő tudásoldal',
    kindEditAccess: 'Szerkesztési hozzáférés kérése',
    kindSharedWithYou: 'Megosztva önnel',
    kindMadePrivate: 'Priváttá téve',
    kindAgentRun: 'Ügynök futása',
    kindAgentVersion: 'Ügynök verziója',
    kindEditGranted: 'Szerkesztési hozzáférés',
    kindAgentOwnership: 'Ügynök tulajdonjoga',
    kindAgentSchedule: 'Ügynök ütemezése',
    kindYourRequest: 'Az ön kérése',
    kindFollowUp: 'Javasolt következő lépés',
    kindResultEdited: 'Eredmény szerkesztve',
    kindMeetingReady: 'Az értekezlet kész',
    kindOther: 'Beérkezett elemek',
    approved: 'Jóváhagyva',
    declined: 'Elutasítva',
    pending: 'Döntésre vár',
    untitled: 'Javasolt változás a vállalatnak',
    pendingFooter: 'Semmi sem változik, amíg jóvá nem hagyja.',
    waitingFooter: 'Egy közzétevő döntésére vár.',
    approveVersion: 'Ennek a verziónak a jóváhagyása',
    decline: 'Elutasítás',
    approvedToast: 'Jóváhagyva.',
    declinedToast: 'Elutasítva.',
    published: 'A jóváhagyott verzió közzétéve.',
    rejected: 'A kérés elutasítva. Az elem változatlan marad.',
    submitted: 'Az ellenőrzésre beküldött verzió. A későbbi változásokhoz új kérés kell.',
    submittedDecided: 'Az ezzel a kéréssel beküldött verzió.',
    technicalDetails: 'Műszaki részletek',
    readFull: 'A teljes szöveg elolvasása',
    previousPage: 'Előző oldal',
    nextPage: 'Következő oldal',
    pageNote: 'Egy oldal a beküldött szövegből.',
    contentStart: 'A beküldött szöveg eleje.',
    tooLong:
      'Ez túl hosszú ahhoz, hogy itt teljesen megjelenjen. Nyissa meg, hogy mindet elolvassa, és ott döntsön.',
    shortened: 'Ez az eleje. Nyissa meg a többi elolvasásához.',
    earlier: 'Korábban',
    others: 'Más hasonlók',
    open: 'Megnyitás',
    openRun: 'A futás megnyitása',
    openMeeting: 'Az értekezlet megnyitása',
    gone: 'Ez már nincs a beérkezett elemek között',
    goneBody: 'Máshol döntöttek róla, visszavonták vagy eltávolították.',
    opening: 'Megnyitás…',
    actionDone: 'Kész.',
    actionFailed: 'Nem sikerült. Próbálja újra.',
    readFailed: 'Nem sikerült olvasottként megjelölni.',
    openFailed: 'Nem sikerült megnyitni ezt a kérést.',
  },
  ga: {
    needsSome: 'Tagann cinntí a iarrann daoine eile ort ar dtús.',
    needsNone: 'Níl aon rud ag fanacht ort anois.',
    loading: 'Do bhosca isteach á lódáil…',
    needsYou: 'Ag fanacht ort',
    updates: 'Nuacht',
    waiting: 'Ag fanacht ar dhaoine eile',
    decisions: 'Cinntí le déanaí',
    decisionsFailed: 'Níorbh fhéidir na cinntí le déanaí a lódáil.',
    markAllRead: 'Marcáil gach rud mar léite',
    emptyTitle: 'Níl aon rud anseo fós',
    emptyBody: 'Tagann athbhreithnithe, faomhadh agus nuacht faoi do chuid oibre anseo.',
    unread: 'Neamhléite',
    latestOf: 'An ceann is déanaí de {count}',
    badgeNeeds: 'Ag fanacht ort: {count}',
    newUpdates: 'Nuacht nua',
    kindSharingReview: 'Athbhreithniú comhroinnte',
    kindSkillPublish: 'Foilsigh Skill',
    kindPage: 'Leathanach eolais le hathbhreithniú',
    kindEditAccess: 'Iarratas ar rochtain eagarthóireachta',
    kindSharedWithYou: 'Comhroinnte leat',
    kindMadePrivate: 'Déanta príobháideach',
    kindAgentRun: 'Rith gníomhaire',
    kindAgentVersion: 'Leagan gníomhaire',
    kindEditGranted: 'Rochtain eagarthóireachta',
    kindAgentOwnership: 'Úinéireacht gníomhaire',
    kindAgentSchedule: 'Sceideal gníomhaire',
    kindYourRequest: 'D’iarratas',
    kindFollowUp: 'An chéad chéim eile atá molta',
    kindResultEdited: 'Toradh curtha in eagar',
    kindMeetingReady: 'Tá an cruinniú réidh',
    kindOther: 'Bosca isteach',
    approved: 'Faofa',
    declined: 'Diúltaithe',
    pending: 'Ag fanacht ar chinneadh',
    untitled: 'Athrú molta don chomhlacht',
    pendingFooter: 'Ní athraíonn aon rud go dtí go bhfaomhann tú é.',
    waitingFooter: 'Ag fanacht ar chinneadh foilsitheora.',
    approveVersion: 'Faomh an leagan seo',
    decline: 'Diúltaigh',
    approvedToast: 'Faofa.',
    declinedToast: 'Diúltaithe.',
    published: 'Foilsíodh an leagan faofa.',
    rejected: 'Diúltaíodh don iarratas. Fanann an mhír gan athrú.',
    submitted:
      'An leagan a cuireadh isteach le haghaidh athbhreithnithe. Teastaíonn iarratas nua le haghaidh athruithe níos déanaí.',
    submittedDecided: 'An leagan a cuireadh isteach leis an iarratas seo.',
    technicalDetails: 'Sonraí teicniúla',
    readFull: 'Léigh an téacs iomlán',
    previousPage: 'An leathanach roimhe',
    nextPage: 'An chéad leathanach eile',
    pageNote: 'Leathanach den téacs a cuireadh isteach.',
    contentStart: 'Tús an téacs a cuireadh isteach.',
    tooLong:
      'Tá sé seo rófhada le taispeáint go hiomlán anseo. Oscail é chun é ar fad a léamh agus cinneadh a dhéanamh ansin.',
    shortened: 'Seo an tús. Oscail é chun an chuid eile a léamh.',
    earlier: 'Níos luaithe',
    others: 'Cinn eile cosúil leis seo',
    open: 'Oscail',
    openRun: 'Oscail an rith',
    openMeeting: 'Oscail an cruinniú',
    gone: 'Níl sé seo i do bhosca isteach a thuilleadh',
    goneBody: 'Rinneadh cinneadh air, tarraingíodh siar é nó baineadh é in áit eile.',
    opening: 'Á oscailt…',
    actionDone: 'Déanta.',
    actionFailed: 'Níor oibrigh sé sin. Bain triail eile as.',
    readFailed: 'Níorbh fhéidir linn é sin a mharcáil mar léite.',
    openFailed: 'Níorbh fhéidir linn an t-iarratas seo a oscailt.',
  },
  it: {
    needsSome: 'Le decisioni che altri ti chiedono vengono per prime.',
    needsNone: 'Al momento niente ti aspetta.',
    loading: 'Caricamento della posta in arrivo…',
    needsYou: 'Aspetta te',
    updates: 'Novità',
    waiting: 'In attesa di altri',
    decisions: 'Decisioni recenti',
    decisionsFailed: 'Impossibile caricare le decisioni recenti.',
    markAllRead: 'Segna tutto come letto',
    emptyTitle: 'Ancora niente qui',
    emptyBody: 'Qui arrivano revisioni, approvazioni e novità sul tuo lavoro.',
    unread: 'Non letto',
    latestOf: 'Il più recente di {count}',
    badgeNeeds: 'Aspetta te: {count}',
    newUpdates: 'Nuove novità',
    kindSharingReview: 'Revisione della condivisione',
    kindSkillPublish: 'Pubblica uno Skill',
    kindPage: 'Pagina di conoscenza da rivedere',
    kindEditAccess: 'Richiesta di accesso in modifica',
    kindSharedWithYou: 'Condiviso con te',
    kindMadePrivate: 'Reso privato',
    kindAgentRun: 'Esecuzione dell’agente',
    kindAgentVersion: 'Versione dell’agente',
    kindEditGranted: 'Accesso in modifica',
    kindAgentOwnership: 'Proprietà dell’agente',
    kindAgentSchedule: 'Pianificazione dell’agente',
    kindYourRequest: 'La tua richiesta',
    kindFollowUp: 'Passo successivo suggerito',
    kindResultEdited: 'Risultato modificato',
    kindMeetingReady: 'Riunione pronta',
    kindOther: 'Posta in arrivo',
    approved: 'Approvato',
    declined: 'Rifiutato',
    pending: 'In attesa di una decisione',
    untitled: 'Modifica proposta per l’azienda',
    pendingFooter: 'Non cambia nulla finché non approvi.',
    waitingFooter: 'In attesa della decisione di chi pubblica.',
    approveVersion: 'Approva questa versione',
    decline: 'Rifiuta',
    approvedToast: 'Approvato.',
    declinedToast: 'Rifiutato.',
    published: 'Versione approvata pubblicata.',
    rejected: 'Richiesta rifiutata. L’elemento resta invariato.',
    submitted:
      'La versione inviata per la revisione. Le modifiche successive richiedono una nuova richiesta.',
    submittedDecided: 'La versione inviata con questa richiesta.',
    technicalDetails: 'Dettagli tecnici',
    readFull: 'Leggi il testo completo',
    previousPage: 'Pagina precedente',
    nextPage: 'Pagina successiva',
    pageNote: 'Una pagina del testo inviato.',
    contentStart: 'L’inizio del testo inviato.',
    tooLong:
      'È troppo lungo per mostrarlo qui per intero. Aprilo per leggerlo tutto e decidere lì.',
    shortened: 'Questo è l’inizio. Aprilo per leggere il resto.',
    earlier: 'In precedenza',
    others: 'Altri simili',
    open: 'Apri',
    openRun: 'Apri l’esecuzione',
    openMeeting: 'Apri la riunione',
    gone: 'Non è più nella tua posta in arrivo',
    goneBody: 'È stato deciso, ritirato o rimosso altrove.',
    opening: 'Apertura…',
    actionDone: 'Fatto.',
    actionFailed: 'Non ha funzionato. Riprova.',
    readFailed: 'Non è stato possibile segnarlo come letto.',
    openFailed: 'Non è stato possibile aprire questa richiesta.',
  },
  lv: {
    needsSome: 'Vispirms lēmumi, ko citi no jums gaida.',
    needsNone: 'Pašlaik nekas negaida jūs.',
    loading: 'Ielādē iesūtni…',
    needsYou: 'Gaida jūs',
    updates: 'Jaunumi',
    waiting: 'Gaida citus',
    decisions: 'Nesenie lēmumi',
    decisionsFailed: 'Neizdevās ielādēt nesenos lēmumus.',
    markAllRead: 'Atzīmēt visu kā izlasītu',
    emptyTitle: 'Šeit vēl nekā nav',
    emptyBody: 'Šeit ienāk pārskatīšanas, apstiprinājumi un jaunumi par jūsu darbu.',
    unread: 'Nelasīts',
    latestOf: 'Jaunākais no {count}',
    badgeNeeds: 'Gaida jūs: {count}',
    newUpdates: 'Jauni jaunumi',
    kindSharingReview: 'Kopīgošanas pārskatīšana',
    kindSkillPublish: 'Skill publicēšana',
    kindPage: 'Pārskatāma zināšanu lapa',
    kindEditAccess: 'Rediģēšanas piekļuves pieprasījums',
    kindSharedWithYou: 'Kopīgots ar jums',
    kindMadePrivate: 'Padarīts privāts',
    kindAgentRun: 'Aģenta palaišana',
    kindAgentVersion: 'Aģenta versija',
    kindEditGranted: 'Rediģēšanas piekļuve',
    kindAgentOwnership: 'Aģenta īpašumtiesības',
    kindAgentSchedule: 'Aģenta grafiks',
    kindYourRequest: 'Jūsu pieprasījums',
    kindFollowUp: 'Ieteiktais nākamais solis',
    kindResultEdited: 'Rezultāts rediģēts',
    kindMeetingReady: 'Sapulce ir gatava',
    kindOther: 'Iesūtne',
    approved: 'Apstiprināts',
    declined: 'Noraidīts',
    pending: 'Gaida lēmumu',
    untitled: 'Ierosinātas izmaiņas uzņēmumam',
    pendingFooter: 'Nekas nemainās, līdz jūs apstiprināt.',
    waitingFooter: 'Gaida publicētāja lēmumu.',
    approveVersion: 'Apstiprināt šo versiju',
    decline: 'Noraidīt',
    approvedToast: 'Apstiprināts.',
    declinedToast: 'Noraidīts.',
    published: 'Apstiprinātā versija publicēta.',
    rejected: 'Pieprasījums noraidīts. Vienums paliek nemainīts.',
    submitted: 'Pārskatīšanai iesniegtā versija. Vēlākām izmaiņām vajag jaunu pieprasījumu.',
    submittedDecided: 'Ar šo pieprasījumu iesniegtā versija.',
    technicalDetails: 'Tehniskā informācija',
    readFull: 'Lasīt visu tekstu',
    previousPage: 'Iepriekšējā lapa',
    nextPage: 'Nākamā lapa',
    pageNote: 'Iesniegtā teksta lapa.',
    contentStart: 'Iesniegtā teksta sākums.',
    tooLong:
      'Tas ir pārāk garš, lai to šeit parādītu pilnībā. Atveriet to, lai izlasītu visu un izlemtu tur.',
    shortened: 'Šis ir sākums. Atveriet, lai izlasītu pārējo.',
    earlier: 'Agrāk',
    others: 'Citi līdzīgi',
    open: 'Atvērt',
    openRun: 'Atvērt palaišanu',
    openMeeting: 'Atvērt sapulci',
    gone: 'Tas vairs nav jūsu iesūtnē',
    goneBody: 'Par to tika izlemts, tas tika atsaukts vai noņemts citur.',
    opening: 'Atver…',
    actionDone: 'Pabeigts.',
    actionFailed: 'Neizdevās. Mēģiniet vēlreiz.',
    readFailed: 'Neizdevās to atzīmēt kā izlasītu.',
    openFailed: 'Neizdevās atvērt šo pieprasījumu.',
  },
  lt: {
    needsSome: 'Pirmiausia sprendimai, kurių iš jūsų prašo kiti.',
    needsNone: 'Šiuo metu niekas jūsų nelaukia.',
    loading: 'Įkeliami gautieji…',
    needsYou: 'Laukia jūsų',
    updates: 'Naujienos',
    waiting: 'Laukia kitų',
    decisions: 'Naujausi sprendimai',
    decisionsFailed: 'Nepavyko įkelti naujausių sprendimų.',
    markAllRead: 'Pažymėti viską kaip perskaitytą',
    emptyTitle: 'Kol kas čia nieko nėra',
    emptyBody: 'Čia atkeliauja peržiūros, patvirtinimai ir naujienos apie jūsų darbą.',
    unread: 'Neperskaityta',
    latestOf: 'Naujausias iš {count}',
    badgeNeeds: 'Laukia jūsų: {count}',
    newUpdates: 'Naujos naujienos',
    kindSharingReview: 'Bendrinimo peržiūra',
    kindSkillPublish: 'Skill paskelbimas',
    kindPage: 'Peržiūrėtinas žinių puslapis',
    kindEditAccess: 'Redagavimo prieigos užklausa',
    kindSharedWithYou: 'Bendrinta su jumis',
    kindMadePrivate: 'Padaryta privačia',
    kindAgentRun: 'Agento vykdymas',
    kindAgentVersion: 'Agento versija',
    kindEditGranted: 'Redagavimo prieiga',
    kindAgentOwnership: 'Agento nuosavybė',
    kindAgentSchedule: 'Agento tvarkaraštis',
    kindYourRequest: 'Jūsų užklausa',
    kindFollowUp: 'Siūlomas kitas žingsnis',
    kindResultEdited: 'Rezultatas redaguotas',
    kindMeetingReady: 'Susitikimas paruoštas',
    kindOther: 'Gautieji',
    approved: 'Patvirtinta',
    declined: 'Atmesta',
    pending: 'Laukia sprendimo',
    untitled: 'Siūlomas pakeitimas įmonei',
    pendingFooter: 'Niekas nesikeis, kol nepatvirtinsite.',
    waitingFooter: 'Laukiama skelbėjo sprendimo.',
    approveVersion: 'Patvirtinti šią versiją',
    decline: 'Atmesti',
    approvedToast: 'Patvirtinta.',
    declinedToast: 'Atmesta.',
    published: 'Patvirtinta versija paskelbta.',
    rejected: 'Užklausa atmesta. Elementas lieka nepakeistas.',
    submitted: 'Peržiūrai pateikta versija. Vėlesniems pakeitimams reikia naujos užklausos.',
    submittedDecided: 'Su šia užklausa pateikta versija.',
    technicalDetails: 'Techninė informacija',
    readFull: 'Skaityti visą tekstą',
    previousPage: 'Ankstesnis puslapis',
    nextPage: 'Kitas puslapis',
    pageNote: 'Pateikto teksto puslapis.',
    contentStart: 'Pateikto teksto pradžia.',
    tooLong:
      'Tai per ilga, kad būtų rodoma čia visa. Atidarykite, kad perskaitytumėte viską ir nuspręstumėte ten.',
    shortened: 'Tai pradžia. Atidarykite, kad perskaitytumėte likusią dalį.',
    earlier: 'Anksčiau',
    others: 'Kiti panašūs',
    open: 'Atidaryti',
    openRun: 'Atidaryti vykdymą',
    openMeeting: 'Atidaryti susitikimą',
    gone: 'To nebėra jūsų gautuosiuose',
    goneBody: 'Tai buvo nuspręsta, atšaukta arba pašalinta kitur.',
    opening: 'Atidaroma…',
    actionDone: 'Atlikta.',
    actionFailed: 'Nepavyko. Bandykite dar kartą.',
    readFailed: 'Nepavyko pažymėti kaip perskaityto.',
    openFailed: 'Nepavyko atidaryti šios užklausos.',
  },
  mt: {
    needsSome: 'Id-deċiżjonijiet li oħrajn jitolbuk jiġu l-ewwel.',
    needsNone: 'Xejn ma qed jistennik bħalissa.',
    loading: 'L-inbox tiegħek qed titgħabba…',
    needsYou: 'Qed jistennik',
    updates: 'Aġġornamenti',
    waiting: 'Qed jistenna lil oħrajn',
    decisions: 'Deċiżjonijiet riċenti',
    decisionsFailed: 'Id-deċiżjonijiet riċenti ma setgħux jitgħabbew.',
    markAllRead: 'Immarka kollox bħala moqri',
    emptyTitle: 'Għad m’hawn xejn',
    emptyBody:
      'Ir-reviżjonijiet, l-approvazzjonijiet u l-aħbarijiet dwar ix-xogħol tiegħek jaslu hawn.',
    unread: 'Mhux moqri',
    latestOf: 'L-aħħar minn {count}',
    badgeNeeds: 'Qed jistennik: {count}',
    newUpdates: 'Aġġornamenti ġodda',
    kindSharingReview: 'Reviżjoni tal-qsim',
    kindSkillPublish: 'Ippubblika Skill',
    kindPage: 'Paġna tal-għarfien għal reviżjoni',
    kindEditAccess: 'Talba għal aċċess għall-editjar',
    kindSharedWithYou: 'Maqsum miegħek',
    kindMadePrivate: 'Sar privat',
    kindAgentRun: 'Tħaddim tal-aġent',
    kindAgentVersion: 'Verżjoni tal-aġent',
    kindEditGranted: 'Aċċess għall-editjar',
    kindAgentOwnership: 'Sjieda tal-aġent',
    kindAgentSchedule: 'Skeda tal-aġent',
    kindYourRequest: 'It-talba tiegħek',
    kindFollowUp: 'Pass li jmiss issuġġerit',
    kindResultEdited: 'Riżultat editjat',
    kindMeetingReady: 'Il-laqgħa lesta',
    kindOther: 'Inbox',
    approved: 'Approvat',
    declined: 'Miċħud',
    pending: 'Qed jistenna deċiżjoni',
    untitled: 'Bidla proposta għall-kumpanija',
    pendingFooter: 'Xejn ma jinbidel sakemm tapprova.',
    waitingFooter: 'Qed jistenna deċiżjoni minn min jippubblika.',
    approveVersion: 'Approva din il-verżjoni',
    decline: 'Iċħad',
    approvedToast: 'Approvat.',
    declinedToast: 'Miċħud.',
    published: 'Il-verżjoni approvata ġiet ippubblikata.',
    rejected: 'It-talba ġiet miċħuda. L-oġġett jibqa’ l-istess.',
    submitted: 'Il-verżjoni mibgħuta għal reviżjoni. Bidliet aktar tard jeħtieġu talba ġdida.',
    submittedDecided: 'Il-verżjoni mibgħuta ma’ din it-talba.',
    technicalDetails: 'Dettalji tekniċi',
    readFull: 'Aqra t-test kollu',
    previousPage: 'Paġna preċedenti',
    nextPage: 'Paġna li jmiss',
    pageNote: 'Paġna mit-test mibgħut.',
    contentStart: 'Il-bidu tat-test mibgħut.',
    tooLong:
      'Dan huwa twil wisq biex jintwera kollu hawn. Iftħu biex taqrah kollu u tiddeċiedi hemmhekk.',
    shortened: 'Dan huwa l-bidu. Iftħu biex taqra l-bqija.',
    earlier: 'Qabel',
    others: 'Oħrajn bħal dan',
    open: 'Iftaħ',
    openRun: 'Iftaħ it-tħaddim',
    openMeeting: 'Iftaħ il-laqgħa',
    gone: 'Dan m’għadux fl-inbox tiegħek',
    goneBody: 'Ġie deċiż, irtirat jew imneħħi x’imkien ieħor.',
    opening: 'Qed jinfetaħ…',
    actionDone: 'Lest.',
    actionFailed: 'Ma ħadimx. Erġa’ pprova.',
    readFailed: 'Ma stajniex nimmarkawh bħala moqri.',
    openFailed: 'Ma stajniex niftħu din it-talba.',
  },
  pl: {
    needsSome: 'Najpierw decyzje, o które proszą cię inni.',
    needsNone: 'Teraz nic na ciebie nie czeka.',
    loading: 'Wczytywanie skrzynki odbiorczej…',
    needsYou: 'Czeka na ciebie',
    updates: 'Nowości',
    waiting: 'Czeka na innych',
    decisions: 'Ostatnie decyzje',
    decisionsFailed: 'Nie udało się wczytać ostatnich decyzji.',
    markAllRead: 'Oznacz wszystko jako przeczytane',
    emptyTitle: 'Na razie nic tu nie ma',
    emptyBody: 'Tu trafiają przeglądy, zatwierdzenia i nowości o twojej pracy.',
    unread: 'Nieprzeczytane',
    latestOf: 'Najnowsze z {count}',
    badgeNeeds: 'Czeka na ciebie: {count}',
    newUpdates: 'Nowe nowości',
    kindSharingReview: 'Przegląd udostępnienia',
    kindSkillPublish: 'Publikacja Skilla',
    kindPage: 'Strona wiedzy do przeglądu',
    kindEditAccess: 'Prośba o dostęp do edycji',
    kindSharedWithYou: 'Udostępnione tobie',
    kindMadePrivate: 'Ustawione jako prywatne',
    kindAgentRun: 'Uruchomienie agenta',
    kindAgentVersion: 'Wersja agenta',
    kindEditGranted: 'Dostęp do edycji',
    kindAgentOwnership: 'Własność agenta',
    kindAgentSchedule: 'Harmonogram agenta',
    kindYourRequest: 'Twoja prośba',
    kindFollowUp: 'Proponowany następny krok',
    kindResultEdited: 'Wynik edytowany',
    kindMeetingReady: 'Spotkanie gotowe',
    kindOther: 'Skrzynka odbiorcza',
    approved: 'Zatwierdzono',
    declined: 'Odrzucono',
    pending: 'Czeka na decyzję',
    untitled: 'Proponowana zmiana dla firmy',
    pendingFooter: 'Nic się nie zmieni, dopóki nie zatwierdzisz.',
    waitingFooter: 'Czeka na decyzję osoby publikującej.',
    approveVersion: 'Zatwierdź tę wersję',
    decline: 'Odrzuć',
    approvedToast: 'Zatwierdzono.',
    declinedToast: 'Odrzucono.',
    published: 'Zatwierdzona wersja została opublikowana.',
    rejected: 'Prośba odrzucona. Element pozostaje bez zmian.',
    submitted: 'Wersja przesłana do przeglądu. Późniejsze zmiany wymagają nowej prośby.',
    submittedDecided: 'Wersja przesłana z tą prośbą.',
    technicalDetails: 'Szczegóły techniczne',
    readFull: 'Przeczytaj cały tekst',
    previousPage: 'Poprzednia strona',
    nextPage: 'Następna strona',
    pageNote: 'Strona przesłanego tekstu.',
    contentStart: 'Początek przesłanego tekstu.',
    tooLong:
      'To jest za długie, aby pokazać tu całość. Otwórz, aby przeczytać wszystko i tam zdecydować.',
    shortened: 'To jest początek. Otwórz, aby przeczytać resztę.',
    earlier: 'Wcześniej',
    others: 'Inne podobne',
    open: 'Otwórz',
    openRun: 'Otwórz uruchomienie',
    openMeeting: 'Otwórz spotkanie',
    gone: 'Tego nie ma już w twojej skrzynce odbiorczej',
    goneBody: 'Zostało rozstrzygnięte, wycofane lub usunięte gdzie indziej.',
    opening: 'Otwieranie…',
    actionDone: 'Gotowe.',
    actionFailed: 'Nie udało się. Spróbuj ponownie.',
    readFailed: 'Nie udało się oznaczyć tego jako przeczytanego.',
    openFailed: 'Nie udało się otworzyć tej prośby.',
  },
  pt: {
    needsSome: 'As decisões que outros lhe pedem vêm primeiro.',
    needsNone: 'Nada está à sua espera neste momento.',
    loading: 'A carregar a caixa de entrada…',
    needsYou: 'À sua espera',
    updates: 'Novidades',
    waiting: 'À espera de outros',
    decisions: 'Decisões recentes',
    decisionsFailed: 'Não foi possível carregar as decisões recentes.',
    markAllRead: 'Marcar tudo como lido',
    emptyTitle: 'Ainda não há nada aqui',
    emptyBody: 'Revisões, aprovações e novidades sobre o seu trabalho chegam aqui.',
    unread: 'Não lido',
    latestOf: 'O mais recente de {count}',
    badgeNeeds: 'À sua espera: {count}',
    newUpdates: 'Novidades por ler',
    kindSharingReview: 'Revisão de partilha',
    kindSkillPublish: 'Publicar um Skill',
    kindPage: 'Página de conhecimento para rever',
    kindEditAccess: 'Pedido de acesso de edição',
    kindSharedWithYou: 'Partilhado consigo',
    kindMadePrivate: 'Tornado privado',
    kindAgentRun: 'Execução do agente',
    kindAgentVersion: 'Versão do agente',
    kindEditGranted: 'Acesso de edição',
    kindAgentOwnership: 'Propriedade do agente',
    kindAgentSchedule: 'Agendamento do agente',
    kindYourRequest: 'O seu pedido',
    kindFollowUp: 'Próximo passo sugerido',
    kindResultEdited: 'Resultado editado',
    kindMeetingReady: 'Reunião pronta',
    kindOther: 'Caixa de entrada',
    approved: 'Aprovado',
    declined: 'Recusado',
    pending: 'À espera de uma decisão',
    untitled: 'Alteração proposta para a empresa',
    pendingFooter: 'Nada muda até aprovar.',
    waitingFooter: 'À espera da decisão de quem publica.',
    approveVersion: 'Aprovar esta versão',
    decline: 'Recusar',
    approvedToast: 'Aprovado.',
    declinedToast: 'Recusado.',
    published: 'Versão aprovada publicada.',
    rejected: 'Pedido recusado. O item mantém-se inalterado.',
    submitted: 'A versão enviada para revisão. Alterações posteriores precisam de um novo pedido.',
    submittedDecided: 'A versão enviada com este pedido.',
    technicalDetails: 'Detalhes técnicos',
    readFull: 'Ler o texto completo',
    previousPage: 'Página anterior',
    nextPage: 'Página seguinte',
    pageNote: 'Uma página do texto enviado.',
    contentStart: 'O início do texto enviado.',
    tooLong:
      'Isto é demasiado longo para ser mostrado aqui por inteiro. Abra-o para ler tudo e decidir lá.',
    shortened: 'Este é o início. Abra-o para ler o resto.',
    earlier: 'Anteriormente',
    others: 'Outros semelhantes',
    open: 'Abrir',
    openRun: 'Abrir a execução',
    openMeeting: 'Abrir a reunião',
    gone: 'Isto já não está na sua caixa de entrada',
    goneBody: 'Foi decidido, retirado ou removido noutro lugar.',
    opening: 'A abrir…',
    actionDone: 'Concluído.',
    actionFailed: 'Não funcionou. Tente novamente.',
    readFailed: 'Não foi possível marcar como lido.',
    openFailed: 'Não foi possível abrir este pedido.',
  },
  ro: {
    needsSome: 'Deciziile pe care ți le cer alții sunt primele.',
    needsNone: 'Acum nu te așteaptă nimic.',
    loading: 'Se încarcă mesajele primite…',
    needsYou: 'Te așteaptă',
    updates: 'Noutăți',
    waiting: 'Așteaptă pe alții',
    decisions: 'Decizii recente',
    decisionsFailed: 'Deciziile recente nu au putut fi încărcate.',
    markAllRead: 'Marchează tot ca citit',
    emptyTitle: 'Încă nu e nimic aici',
    emptyBody: 'Aici sosesc verificări, aprobări și noutăți despre munca ta.',
    unread: 'Necitit',
    latestOf: 'Cel mai recent din {count}',
    badgeNeeds: 'Te așteaptă: {count}',
    newUpdates: 'Noutăți noi',
    kindSharingReview: 'Verificarea partajării',
    kindSkillPublish: 'Publicarea unui Skill',
    kindPage: 'Pagină de cunoștințe de verificat',
    kindEditAccess: 'Cerere de acces la editare',
    kindSharedWithYou: 'Partajat cu tine',
    kindMadePrivate: 'Făcut privat',
    kindAgentRun: 'Rulare agent',
    kindAgentVersion: 'Versiune agent',
    kindEditGranted: 'Acces la editare',
    kindAgentOwnership: 'Proprietatea agentului',
    kindAgentSchedule: 'Programul agentului',
    kindYourRequest: 'Cererea ta',
    kindFollowUp: 'Pas următor sugerat',
    kindResultEdited: 'Rezultat editat',
    kindMeetingReady: 'Ședința e gata',
    kindOther: 'Mesaje primite',
    approved: 'Aprobat',
    declined: 'Respins',
    pending: 'Așteaptă o decizie',
    untitled: 'Modificare propusă pentru companie',
    pendingFooter: 'Nimic nu se schimbă până nu aprobi.',
    waitingFooter: 'Așteaptă decizia unui editor.',
    approveVersion: 'Aprobă această versiune',
    decline: 'Respinge',
    approvedToast: 'Aprobat.',
    declinedToast: 'Respins.',
    published: 'Versiunea aprobată a fost publicată.',
    rejected: 'Cererea a fost respinsă. Elementul rămâne neschimbat.',
    submitted: 'Versiunea trimisă spre verificare. Modificările ulterioare necesită o cerere nouă.',
    submittedDecided: 'Versiunea trimisă cu această cerere.',
    technicalDetails: 'Detalii tehnice',
    readFull: 'Citește tot textul',
    previousPage: 'Pagina anterioară',
    nextPage: 'Pagina următoare',
    pageNote: 'O pagină din textul trimis.',
    contentStart: 'Începutul textului trimis.',
    tooLong:
      'Este prea lung pentru a fi afișat aici integral. Deschide-l ca să citești tot și să decizi acolo.',
    shortened: 'Acesta este începutul. Deschide-l ca să citești restul.',
    earlier: 'Mai devreme',
    others: 'Altele asemănătoare',
    open: 'Deschide',
    openRun: 'Deschide rularea',
    openMeeting: 'Deschide ședința',
    gone: 'Acesta nu mai este în mesajele tale primite',
    goneBody: 'A fost decis, retras sau eliminat în altă parte.',
    opening: 'Se deschide…',
    actionDone: 'Gata.',
    actionFailed: 'Nu a funcționat. Încearcă din nou.',
    readFailed: 'Nu am putut marca acest element ca citit.',
    openFailed: 'Nu am putut deschide această cerere.',
  },
  sk: {
    needsSome: 'Najprv rozhodnutia, o ktoré vás žiadajú ostatní.',
    needsNone: 'Teraz na vás nič nečaká.',
    loading: 'Načítava sa doručená pošta…',
    needsYou: 'Čaká na vás',
    updates: 'Novinky',
    waiting: 'Čaká na ostatných',
    decisions: 'Nedávne rozhodnutia',
    decisionsFailed: 'Nedávne rozhodnutia sa nepodarilo načítať.',
    markAllRead: 'Označiť všetko ako prečítané',
    emptyTitle: 'Zatiaľ tu nič nie je',
    emptyBody: 'Sem prichádzajú kontroly, schválenia a novinky o vašej práci.',
    unread: 'Neprečítané',
    latestOf: 'Najnovšie z {count}',
    badgeNeeds: 'Čaká na vás: {count}',
    newUpdates: 'Nové novinky',
    kindSharingReview: 'Kontrola zdieľania',
    kindSkillPublish: 'Zverejnenie Skillu',
    kindPage: 'Stránka znalostí na kontrolu',
    kindEditAccess: 'Žiadosť o prístup na úpravy',
    kindSharedWithYou: 'Zdieľané s vami',
    kindMadePrivate: 'Nastavené ako súkromné',
    kindAgentRun: 'Beh agenta',
    kindAgentVersion: 'Verzia agenta',
    kindEditGranted: 'Prístup na úpravy',
    kindAgentOwnership: 'Vlastníctvo agenta',
    kindAgentSchedule: 'Plán agenta',
    kindYourRequest: 'Vaša žiadosť',
    kindFollowUp: 'Navrhovaný ďalší krok',
    kindResultEdited: 'Výsledok upravený',
    kindMeetingReady: 'Stretnutie je pripravené',
    kindOther: 'Doručená pošta',
    approved: 'Schválené',
    declined: 'Zamietnuté',
    pending: 'Čaká na rozhodnutie',
    untitled: 'Navrhovaná zmena pre firmu',
    pendingFooter: 'Nič sa nezmení, kým neschválite.',
    waitingFooter: 'Čaká na rozhodnutie zverejňujúceho.',
    approveVersion: 'Schváliť túto verziu',
    decline: 'Zamietnuť',
    approvedToast: 'Schválené.',
    declinedToast: 'Zamietnuté.',
    published: 'Schválená verzia je zverejnená.',
    rejected: 'Žiadosť zamietnutá. Položka zostáva bez zmeny.',
    submitted: 'Verzia odoslaná na kontrolu. Neskoršie zmeny vyžadujú novú žiadosť.',
    submittedDecided: 'Verzia odoslaná s touto žiadosťou.',
    technicalDetails: 'Technické podrobnosti',
    readFull: 'Prečítať celý text',
    previousPage: 'Predchádzajúca strana',
    nextPage: 'Ďalšia strana',
    pageNote: 'Strana odoslaného textu.',
    contentStart: 'Začiatok odoslaného textu.',
    tooLong:
      'Toto je príliš dlhé, aby sa to tu zobrazilo celé. Otvorte to, prečítajte si to a rozhodnite tam.',
    shortened: 'Toto je začiatok. Otvorte to a prečítajte si zvyšok.',
    earlier: 'Skôr',
    others: 'Ďalšie podobné',
    open: 'Otvoriť',
    openRun: 'Otvoriť beh',
    openMeeting: 'Otvoriť stretnutie',
    gone: 'Toto už vo vašej doručenej pošte nie je',
    goneBody: 'Bolo to rozhodnuté, stiahnuté alebo odstránené inde.',
    opening: 'Otvára sa…',
    actionDone: 'Hotovo.',
    actionFailed: 'Nepodarilo sa to. Skúste to znova.',
    readFailed: 'Nepodarilo sa to označiť ako prečítané.',
    openFailed: 'Túto žiadosť sa nepodarilo otvoriť.',
  },
  sl: {
    needsSome: 'Najprej odločitve, ki jih drugi pričakujejo od vas.',
    needsNone: 'Trenutno vas nič ne čaka.',
    loading: 'Nalaganje prejete pošte…',
    needsYou: 'Čaka vas',
    updates: 'Novice',
    waiting: 'Čaka druge',
    decisions: 'Nedavne odločitve',
    decisionsFailed: 'Nedavnih odločitev ni bilo mogoče naložiti.',
    markAllRead: 'Označi vse kot prebrano',
    emptyTitle: 'Tu še ni ničesar',
    emptyBody: 'Sem prihajajo pregledi, odobritve in novice o vašem delu.',
    unread: 'Neprebrano',
    latestOf: 'Najnovejše od {count}',
    badgeNeeds: 'Čaka vas: {count}',
    newUpdates: 'Nove novice',
    kindSharingReview: 'Pregled deljenja',
    kindSkillPublish: 'Objava Skilla',
    kindPage: 'Stran znanja za pregled',
    kindEditAccess: 'Zahteva za dostop za urejanje',
    kindSharedWithYou: 'Deljeno z vami',
    kindMadePrivate: 'Nastavljeno kot zasebno',
    kindAgentRun: 'Zagon agenta',
    kindAgentVersion: 'Različica agenta',
    kindEditGranted: 'Dostop za urejanje',
    kindAgentOwnership: 'Lastništvo agenta',
    kindAgentSchedule: 'Urnik agenta',
    kindYourRequest: 'Vaša zahteva',
    kindFollowUp: 'Predlagani naslednji korak',
    kindResultEdited: 'Rezultat urejen',
    kindMeetingReady: 'Sestanek je pripravljen',
    kindOther: 'Prejeta pošta',
    approved: 'Odobreno',
    declined: 'Zavrnjeno',
    pending: 'Čaka na odločitev',
    untitled: 'Predlagana sprememba za podjetje',
    pendingFooter: 'Nič se ne spremeni, dokler ne odobrite.',
    waitingFooter: 'Čaka na odločitev objavitelja.',
    approveVersion: 'Odobri to različico',
    decline: 'Zavrni',
    approvedToast: 'Odobreno.',
    declinedToast: 'Zavrnjeno.',
    published: 'Odobrena različica je objavljena.',
    rejected: 'Zahteva je zavrnjena. Element ostane nespremenjen.',
    submitted: 'Različica, poslana v pregled. Poznejše spremembe potrebujejo novo zahtevo.',
    submittedDecided: 'Različica, poslana s to zahtevo.',
    technicalDetails: 'Tehnične podrobnosti',
    readFull: 'Preberi celotno besedilo',
    previousPage: 'Prejšnja stran',
    nextPage: 'Naslednja stran',
    pageNote: 'Stran poslanega besedila.',
    contentStart: 'Začetek poslanega besedila.',
    tooLong:
      'To je predolgo, da bi bilo tukaj prikazano v celoti. Odprite, da preberete vse in se tam odločite.',
    shortened: 'To je začetek. Odprite, da preberete preostanek.',
    earlier: 'Prej',
    others: 'Druga podobna',
    open: 'Odpri',
    openRun: 'Odpri zagon',
    openMeeting: 'Odpri sestanek',
    gone: 'Tega ni več v vaši prejeti pošti',
    goneBody: 'O tem je bilo odločeno, umaknjeno ali odstranjeno drugje.',
    opening: 'Odpiranje…',
    actionDone: 'Končano.',
    actionFailed: 'Ni uspelo. Poskusite znova.',
    readFailed: 'Tega nismo mogli označiti kot prebrano.',
    openFailed: 'Te zahteve nismo mogli odpreti.',
  },
  es: {
    needsSome: 'Las decisiones que otros te piden van primero.',
    needsNone: 'Ahora mismo no hay nada esperándote.',
    loading: 'Cargando tu bandeja de entrada…',
    needsYou: 'Te espera',
    updates: 'Novedades',
    waiting: 'Esperando a otros',
    decisions: 'Decisiones recientes',
    decisionsFailed: 'No se pudieron cargar las decisiones recientes.',
    markAllRead: 'Marcar todo como leído',
    emptyTitle: 'Todavía no hay nada aquí',
    emptyBody: 'Aquí llegan revisiones, aprobaciones y novedades sobre tu trabajo.',
    unread: 'No leído',
    latestOf: 'El más reciente de {count}',
    badgeNeeds: 'Te espera: {count}',
    newUpdates: 'Novedades sin leer',
    kindSharingReview: 'Revisión de uso compartido',
    kindSkillPublish: 'Publicar un Skill',
    kindPage: 'Página de conocimiento por revisar',
    kindEditAccess: 'Solicitud de acceso de edición',
    kindSharedWithYou: 'Compartido contigo',
    kindMadePrivate: 'Hecho privado',
    kindAgentRun: 'Ejecución del agente',
    kindAgentVersion: 'Versión del agente',
    kindEditGranted: 'Acceso de edición',
    kindAgentOwnership: 'Propiedad del agente',
    kindAgentSchedule: 'Programación del agente',
    kindYourRequest: 'Tu solicitud',
    kindFollowUp: 'Siguiente paso sugerido',
    kindResultEdited: 'Resultado editado',
    kindMeetingReady: 'Reunión lista',
    kindOther: 'Bandeja de entrada',
    approved: 'Aprobado',
    declined: 'Rechazado',
    pending: 'Esperando una decisión',
    untitled: 'Cambio propuesto para la empresa',
    pendingFooter: 'Nada cambia hasta que lo apruebes.',
    waitingFooter: 'Esperando la decisión de quien publica.',
    approveVersion: 'Aprobar esta versión',
    decline: 'Rechazar',
    approvedToast: 'Aprobado.',
    declinedToast: 'Rechazado.',
    published: 'Versión aprobada publicada.',
    rejected: 'Solicitud rechazada. El elemento no cambia.',
    submitted:
      'La versión enviada a revisión. Los cambios posteriores necesitan una nueva solicitud.',
    submittedDecided: 'La versión enviada con esta solicitud.',
    technicalDetails: 'Detalles técnicos',
    readFull: 'Leer el texto completo',
    previousPage: 'Página anterior',
    nextPage: 'Página siguiente',
    pageNote: 'Una página del texto enviado.',
    contentStart: 'El comienzo del texto enviado.',
    tooLong:
      'Es demasiado largo para mostrarlo aquí completo. Ábrelo para leerlo todo y decidir allí.',
    shortened: 'Este es el comienzo. Ábrelo para leer el resto.',
    earlier: 'Antes',
    others: 'Otros parecidos',
    open: 'Abrir',
    openRun: 'Abrir la ejecución',
    openMeeting: 'Abrir la reunión',
    gone: 'Esto ya no está en tu bandeja de entrada',
    goneBody: 'Se decidió, retiró o eliminó en otro lugar.',
    opening: 'Abriendo…',
    actionDone: 'Hecho.',
    actionFailed: 'No funcionó. Inténtalo de nuevo.',
    readFailed: 'No pudimos marcarlo como leído.',
    openFailed: 'No pudimos abrir esta solicitud.',
  },
  sv: {
    needsSome: 'Beslut som andra ber dig om kommer först.',
    needsNone: 'Inget väntar på dig just nu.',
    loading: 'Läser in din inkorg…',
    needsYou: 'Väntar på dig',
    updates: 'Nyheter',
    waiting: 'Väntar på andra',
    decisions: 'Senaste besluten',
    decisionsFailed: 'De senaste besluten kunde inte läsas in.',
    markAllRead: 'Markera allt som läst',
    emptyTitle: 'Inget här ännu',
    emptyBody: 'Granskningar, godkännanden och nyheter om ditt arbete hamnar här.',
    unread: 'Oläst',
    latestOf: 'Senaste av {count}',
    badgeNeeds: 'Väntar på dig: {count}',
    newUpdates: 'Nya nyheter',
    kindSharingReview: 'Granskning av delning',
    kindSkillPublish: 'Publicera en Skill',
    kindPage: 'Kunskapssida att granska',
    kindEditAccess: 'Begäran om redigeringsbehörighet',
    kindSharedWithYou: 'Delat med dig',
    kindMadePrivate: 'Gjord privat',
    kindAgentRun: 'Agentkörning',
    kindAgentVersion: 'Agentversion',
    kindEditGranted: 'Redigeringsbehörighet',
    kindAgentOwnership: 'Ägarskap för agent',
    kindAgentSchedule: 'Agentens schema',
    kindYourRequest: 'Din begäran',
    kindFollowUp: 'Föreslaget nästa steg',
    kindResultEdited: 'Resultat redigerat',
    kindMeetingReady: 'Mötet är klart',
    kindOther: 'Inkorg',
    approved: 'Godkänd',
    declined: 'Avböjd',
    pending: 'Väntar på beslut',
    untitled: 'Föreslagen ändring för företaget',
    pendingFooter: 'Inget ändras förrän du godkänner.',
    waitingFooter: 'Väntar på att en publicerare beslutar.',
    approveVersion: 'Godkänn den här versionen',
    decline: 'Avböj',
    approvedToast: 'Godkänt.',
    declinedToast: 'Avböjt.',
    published: 'Den godkända versionen är publicerad.',
    rejected: 'Begäran avböjdes. Objektet är oförändrat.',
    submitted: 'Versionen som skickades för granskning. Senare ändringar kräver en ny begäran.',
    submittedDecided: 'Versionen som skickades med den här begäran.',
    technicalDetails: 'Tekniska detaljer',
    readFull: 'Läs hela texten',
    previousPage: 'Föregående sida',
    nextPage: 'Nästa sida',
    pageNote: 'En sida av den inskickade texten.',
    contentStart: 'Början av den inskickade texten.',
    tooLong:
      'Det här är för långt för att visas helt här. Öppna det för att läsa allt och besluta där.',
    shortened: 'Det här är början. Öppna det för att läsa resten.',
    earlier: 'Tidigare',
    others: 'Andra likadana',
    open: 'Öppna',
    openRun: 'Öppna körningen',
    openMeeting: 'Öppna mötet',
    gone: 'Det här finns inte längre i din inkorg',
    goneBody: 'Det har beslutats, dragits tillbaka eller tagits bort någon annanstans.',
    opening: 'Öppnar…',
    actionDone: 'Klart.',
    actionFailed: 'Det gick inte. Försök igen.',
    readFailed: 'Vi kunde inte markera det som läst.',
    openFailed: 'Vi kunde inte öppna den här begäran.',
  },
};

export default inboxCopy;
